import React from 'react';
import { PieChart, Pie, Cell } from 'recharts';

// Firebase
import { db } from "../../firebaseConfig";

// global
import { useAuth } from '../../AuthContext';

// redux
import { useAppSelector } from '../../redux/store';

// function fetchCallData
import { fetchCallMetrics } from './Function';
import getCurrentScreen from '../../Helper/GetScreen';

interface UserOption {
  id: string;
  name: string;
}

interface CallDistributionProps {
  // Add props here
  users: UserOption[];
  selectedUser: UserOption[];
  startTime: string | null;
  endTime: string | null;
}

interface CallMetrics {
  outboundCalls: number;
  missedCalls: number;
  connected: number;
  inboundCalls: number;
  outboundDuration: number;  // in seconds
  inboundDuration: number;   // in seconds
  totalDuration: number;     // in seconds
  durationZero: number;
  durationNotZero: number;
}


const ConnectionRatio: React.FC<CallDistributionProps> = ({ users, selectedUser, startTime, endTime }) => {
  const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

  // data state
  const [callMetrics, setCallMetrics] = React.useState<CallMetrics>();
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [tooltipContent, setTooltipContent] = React.useState<{ x: number; y: number; content: string } | null>(null);

  React.useEffect(() => {
    fetchCallMetrics({ db, userData, startTime: startTime || null, endTime: endTime || null, users, currentScreen, selectedUser })
      .then((data) => { setCallMetrics(data); })
      .catch((error) => console.error("Error fetching call logs:", error));
  }, [userData?.companyId, startTime, endTime, users, currentScreen, selectedUser]);

  const data = [
    { name: 'Not Connected', value: callMetrics?.durationZero || 0 },
    { name: 'Connected', value: callMetrics?.durationNotZero || 0 },
  ];

  const COLORS = ['#ef4444', '#0891b2'];

  const calculatePercentage = (value: number | undefined) => {
    if (!callMetrics?.durationZero && !callMetrics?.durationNotZero) return 0;
    const total = (callMetrics?.durationZero || 0) + (callMetrics?.durationNotZero || 0);
    return ((value || 0) / total * 100).toFixed(1);
  };

  const handleMouseEvent = (state: string, event: any) => {
    if (!event || !event.payload) return;
    
    const index = data.findIndex(item => item.name === event.payload.name);
    
    if (state === 'enter') {
      setActiveIndex(index);
    } else if (state === 'leave') {
      setActiveIndex(null);
      setTooltipContent(null);
      return;
    }
    
    const percentage = calculatePercentage(event.payload.value);
    setTooltipContent({
      x: event.chartX,
      y: event.chartY,
      content: `${event.payload.name}: ${event.payload.value} (${percentage}%)`
    });
  };

  return (
    <div className="bg-white relative">
      <h2
        className="text-xl text-black font-medium text-center"
        style={{
          fontFamily: "Montserrat",
          lineHeight: "29.26px",
        }}
      >Connection Ratio</h2>

      <div className="flex justify-center relative">
        <PieChart width={200} height={200}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={0}
            outerRadius={80}
            dataKey="value"
            onMouseEnter={(e) => handleMouseEvent('enter', e)}
            onMouseLeave={(e) => handleMouseEvent('leave', e)}
            onMouseMove={(e) => handleMouseEvent('move', e)}
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index]}
                style={{
                  cursor: 'pointer',
                  transform: activeIndex === index ? 'scale(1.1)' : 'scale(1)',
                  transformOrigin: '50% 50%',
                  transition: 'transform 0.3s ease-in-out',
                  outline: 'none'
                }}
                className="focus:outline-none"
              />
            ))}
          </Pie>
        </PieChart>
        {tooltipContent && (
          <div
            style={{
              position: 'fixed',
              left: tooltipContent.x + 10,
              top: tooltipContent.y - 20,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              pointerEvents: 'none',
              zIndex: 1000,
            }}
          >
            {tooltipContent.content}
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 mt-4 text-xs">
        <div className="flex items-center ">
          <div className="w-2 h-2 rounded-full bg-cyan-600 mr-2"></div>
          Connected
        </div>
        <div className="flex items-center ">
          <div className="w-2 h-2  rounded-full bg-red-500 mr-2"></div>
          Not Connected
        </div>
      </div>
    </div>
  );
};

export default ConnectionRatio;